import {
  type ProductStockCard,
  type ProductSupplier,
  STOCK_MOVEMENT_TYPE_LABELS,
  type StationProductSetting,
} from "@station/contracts";
import { Alert, Button, Card, MoneyText, SkeletonRows, StatusBadge } from "@station/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeftRight, CalendarX2, Pencil, Plus } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { ProductSupplierDialog, StationSettingDialog } from "../../../../features/market/catalog-dialogs.js";
import {
  formatSignedQuantity,
  formatStockQuantity,
  movementSourceKey,
} from "../../../../features/market/market-values.js";
import { useMarketPermissions, useProductStockCard } from "../../../../features/market/queries.js";
import {
  crumbLink,
  ExpiryBadge,
  Field,
  MarketPage,
  marketSearchShape,
  MarketStationField,
  SourceDocLink,
  TableFrame,
  useMarketStations,
} from "../../../../features/market/shared.js";
import { Section } from "../../../../features/expenses/shared.js";
import { Th } from "../../../../features/fuel/shared.js";
import { errorMessage } from "../../../../lib/errors.js";
import { formatDate, formatDateTime, formatUnitPrice } from "../../../../lib/format.js";

const searchSchema = z.object({ ...marketSearchShape });

export const Route = createFileRoute("/_app/market/stok/$productId")({
  validateSearch: (search) => searchSchema.parse(search),
  component: ProductStockCardRoute,
});

/**
 * Ürün stok kartı (Prompt 16 — web): depolar, açık partiler (FEFO sırası, SKT rozeti), son 20 hareket, tedarikçiler
 * ve istasyon ürün ayarları. Ürünün market ayrıntısı BURADADIR: tedarikçi bağı ve istasyon ayarı (min / max, raf
 * yeri, listede mi) bu sayfadan `market.catalog.manage` ile düzenlenir — ürünün katalog kartı (ad, KDV, barkod)
 * Ürün kataloğu ekranında kalır.
 */
function ProductStockCardRoute(): ReactElement {
  const { t } = useTranslation();
  const { productId } = Route.useParams();
  return (
    <MarketPage
      title={t("market.card.title")}
      breadcrumb={[crumbLink(t("menu.marketStock"), "/market/stok"), { label: t("market.card.title") }]}
    >
      {({ companyId }) => <CardBody companyId={companyId} productId={productId} />}
    </MarketPage>
  );
}

function CardBody({ companyId, productId }: { companyId: string; productId: string }): ReactElement {
  const station = useMarketStations(companyId);
  const card = useProductStockCard(productId, companyId, station.stationId);

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <MarketStationField selection={station} />
      </Card>
      {card.error ? (
        <Alert tone="danger">{errorMessage(card.error)}</Alert>
      ) : !card.data ? (
        <SkeletonRows rows={8} />
      ) : (
        <StockCard card={card.data} stations={station.stations} />
      )}
    </>
  );
}

function StockCard({
  card,
  stations,
}: {
  card: ProductStockCard;
  stations: ReadonlyArray<{ id: string; code: string; name: string }>;
}): ReactElement {
  const { t } = useTranslation();
  const permissions = useMarketPermissions();
  const [supplierDialog, setSupplierDialog] = useState<{ supplier: ProductSupplier | null } | null>(null);
  const [settingDialog, setSettingDialog] = useState<{ setting: StationProductSetting | null } | null>(null);
  const withoutSetting = stations.filter(
    (station) => !card.stationSettings.some((setting) => setting.stationId === station.id),
  );

  return (
    <div className="grid gap-4">
      <Section title={`${card.productCode} · ${card.productName}`}>
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label={t("market.fields.onHand")}>
            <span className="tabular">{formatStockQuantity(card.totals.quantity, card.unit)}</span>
          </Field>
          <Field label={t("market.fields.stockValue")}>
            <MoneyText value={card.totals.value} colorize />
          </Field>
          <Field label={t("market.fields.trackExpiry")}>
            {card.trackExpiry ? t("common.yes") : t("common.no")}
          </Field>
          <Field label={t("market.card.links")}>
            <span className="flex flex-wrap gap-3">
              <Link
                to="/market/hareketler"
                search={{ product: card.productId }}
                className="inline-flex items-center gap-1 underline underline-offset-4"
              >
                <ArrowLeftRight className="size-4" aria-hidden="true" />
                {t("market.card.allMovements")}
              </Link>
              {card.trackExpiry ? (
                <Link
                  to="/market/partiler"
                  search={{ product: card.productId }}
                  className="inline-flex items-center gap-1 underline underline-offset-4"
                >
                  <CalendarX2 className="size-4" aria-hidden="true" />
                  {t("market.card.allLots")}
                </Link>
              ) : null}
            </span>
          </Field>
        </dl>
      </Section>

      <Section title={t("market.card.balances")}>
        {card.balances.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("market.card.noBalances")}</p>
        ) : (
          <TableFrame label={t("market.card.balances")}>
            <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
              <tr>
                <Th>{t("market.fields.warehouse")}</Th>
                <Th align="right">{t("market.fields.onHand")}</Th>
                <Th align="right">{t("market.fields.averageUnitCost")}</Th>
                <Th align="right">{t("market.fields.stockValue")}</Th>
                <Th align="right">{t("market.fields.minMax")}</Th>
                <Th>{t("market.fields.shelfLocation")}</Th>
                <Th>{t("market.fields.lastSaleAt")}</Th>
                <Th>{t("market.fields.lastReceiptAt")}</Th>
                <Th>{t("market.fields.lastCountedAt")}</Th>
              </tr>
            </thead>
            <tbody>
              {card.balances.map((balance) => (
                <tr key={balance.id} className="border-t">
                  <td className="px-3 py-2">
                    <span className="grid gap-0.5">
                      <span>{balance.stationName}</span>
                      <span className="font-mono text-xs text-muted-foreground">
                        {balance.warehouseCode} {balance.warehouseName}
                      </span>
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <span className="grid justify-items-end gap-1">
                      <span className="tabular">{formatStockQuantity(balance.quantity, balance.unit)}</span>
                      {balance.isNegative ? (
                        <StatusBadge tone="danger" className="whitespace-nowrap">
                          {t("market.stock.negativeBadge")}
                        </StatusBadge>
                      ) : balance.belowMin ? (
                        <StatusBadge tone="warning" className="whitespace-nowrap">
                          {t("market.stock.belowMinBadge")}
                        </StatusBadge>
                      ) : null}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right tabular">{formatUnitPrice(balance.averageUnitCost)}</td>
                  <td className="px-3 py-2 text-right">
                    <MoneyText value={balance.value} colorize />
                  </td>
                  <td className="px-3 py-2 text-right tabular whitespace-nowrap">
                    {balance.minStock === null ? "—" : formatStockQuantity(balance.minStock)} /{" "}
                    {balance.maxStock === null ? "—" : formatStockQuantity(balance.maxStock)}
                  </td>
                  <td className="px-3 py-2">{balance.shelfLocation ?? "—"}</td>
                  <td className="px-3 py-2 tabular">{formatDate(balance.lastSaleAt)}</td>
                  <td className="px-3 py-2 tabular">{formatDate(balance.lastReceiptAt)}</td>
                  <td className="px-3 py-2 tabular">{formatDate(balance.lastCountedAt)}</td>
                </tr>
              ))}
            </tbody>
          </TableFrame>
        )}
      </Section>

      {card.trackExpiry || card.lots.length > 0 ? (
        <Section title={t("market.card.lots")}>
          {card.lots.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("market.card.noLots")}</p>
          ) : (
            <TableFrame label={t("market.card.lots")}>
              <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
                <tr>
                  <Th>{t("market.fields.lotNo")}</Th>
                  <Th>{t("market.fields.expiryDate")}</Th>
                  <Th>{t("market.fields.expiryState")}</Th>
                  <Th align="right">{t("market.fields.remaining")}</Th>
                  <Th>{t("market.fields.warehouse")}</Th>
                  <Th>{t("market.fields.receivedAt")}</Th>
                </tr>
              </thead>
              <tbody>
                {card.lots.map((lot) => (
                  <tr key={lot.id} className="border-t">
                    <td className="px-3 py-2 font-mono">{lot.lotNo ?? t("market.lots.noLotNo")}</td>
                    <td className="px-3 py-2 tabular">{formatDate(lot.expiryDate)}</td>
                    <td className="px-3 py-2">
                      <ExpiryBadge state={lot.expiryState} />
                    </td>
                    <td className="px-3 py-2 text-right tabular">
                      {formatStockQuantity(lot.quantity, card.unit)}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{lot.warehouseCode}</td>
                    <td className="px-3 py-2 tabular">{formatDate(lot.receivedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </TableFrame>
          )}
        </Section>
      ) : null}

      <Section title={t("market.card.recentMovements")}>
        {card.recentMovements.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("market.card.noMovements")}</p>
        ) : (
          <TableFrame label={t("market.card.recentMovements")}>
            <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
              <tr>
                <Th>{t("market.fields.occurredAt")}</Th>
                <Th>{t("market.fields.movementType")}</Th>
                <Th align="right">{t("market.fields.quantity")}</Th>
                <Th align="right">{t("market.fields.value")}</Th>
                <Th align="right">{t("market.fields.quantityAfter")}</Th>
                <Th>{t("market.fields.source")}</Th>
              </tr>
            </thead>
            <tbody>
              {card.recentMovements.map((movement) => (
                <tr key={movement.id} className="border-t">
                  <td className="px-3 py-2 tabular whitespace-nowrap">
                    {formatDateTime(movement.occurredAt)}
                  </td>
                  <td className="px-3 py-2">{STOCK_MOVEMENT_TYPE_LABELS[movement.movementType]}</td>
                  <td className="px-3 py-2 text-right tabular whitespace-nowrap">
                    {formatSignedQuantity(movement.quantity, card.unit)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <MoneyText value={movement.value} colorize />
                  </td>
                  <td className="px-3 py-2 text-right tabular whitespace-nowrap">
                    {formatStockQuantity(movement.quantityAfter, card.unit)}
                  </td>
                  <td className="px-3 py-2">
                    <SourceDocLink
                      sourceDocType={movement.sourceDocType}
                      sourceDocId={movement.sourceDocId}
                      label={t(`market.movements.source.${movementSourceKey(movement.sourceDocType)}`)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </TableFrame>
        )}
      </Section>

      <Section
        title={t("market.card.suppliers")}
        actions={
          permissions.canManageCatalog ? (
            <Button variant="outline" size="sm" onClick={() => setSupplierDialog({ supplier: null })}>
              <Plus aria-hidden="true" />
              {t("market.suppliers.add")}
            </Button>
          ) : null
        }
      >
        {card.suppliers.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("market.card.noSuppliers")}</p>
        ) : (
          <TableFrame label={t("market.card.suppliers")}>
            <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
              <tr>
                <Th>{t("market.suppliers.supplier")}</Th>
                <Th>{t("market.suppliers.supplierProductCode")}</Th>
                <Th align="right">{t("market.suppliers.purchasePrice")}</Th>
                <Th align="right">{t("market.suppliers.packSize")}</Th>
                <Th align="right">{t("market.suppliers.leadTimeDays")}</Th>
                <Th>{t("common.status")}</Th>
                {permissions.canManageCatalog ? <Th>{t("common.actions")}</Th> : null}
              </tr>
            </thead>
            <tbody>
              {card.suppliers.map((supplier) => (
                <tr key={supplier.id} className="border-t">
                  <td className="px-3 py-2">
                    <span className="flex flex-wrap items-center gap-2">
                      {supplier.counterpartyName}
                      {supplier.isPreferred ? (
                        <StatusBadge tone="info">{t("market.suppliers.preferred")}</StatusBadge>
                      ) : null}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{supplier.supplierProductCode ?? "—"}</td>
                  <td className="px-3 py-2 text-right tabular">{formatUnitPrice(supplier.purchasePrice)}</td>
                  <td className="px-3 py-2 text-right tabular">
                    {supplier.packSize === null ? "—" : formatStockQuantity(supplier.packSize)}
                  </td>
                  <td className="px-3 py-2 text-right tabular">
                    {t("market.suppliers.days", { count: supplier.leadTimeDays })}
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge tone={supplier.isActive ? "success" : "neutral"}>
                      {supplier.isActive ? t("common.active") : t("common.inactive")}
                    </StatusBadge>
                  </td>
                  {permissions.canManageCatalog ? (
                    <td className="px-3 py-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={t("market.suppliers.editLabel", { name: supplier.counterpartyName })}
                        onClick={() => setSupplierDialog({ supplier })}
                      >
                        <Pencil aria-hidden="true" />
                        {t("common.edit")}
                      </Button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </TableFrame>
        )}
      </Section>

      <Section
        title={t("market.card.stationSettings")}
        actions={
          permissions.canManageCatalog && withoutSetting.length > 0 ? (
            <Button variant="outline" size="sm" onClick={() => setSettingDialog({ setting: null })}>
              <Plus aria-hidden="true" />
              {t("market.stationSettings.add")}
            </Button>
          ) : null
        }
      >
        {card.stationSettings.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("market.card.noStationSettings")}</p>
        ) : (
          <TableFrame label={t("market.card.stationSettings")}>
            <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
              <tr>
                <Th>{t("market.fields.station")}</Th>
                <Th align="right">{t("market.fields.minStock")}</Th>
                <Th align="right">{t("market.fields.maxStock")}</Th>
                <Th>{t("market.fields.shelfLocation")}</Th>
                <Th>{t("market.fields.isListed")}</Th>
                {permissions.canManageCatalog ? <Th>{t("common.actions")}</Th> : null}
              </tr>
            </thead>
            <tbody>
              {card.stationSettings.map((setting) => {
                // İstasyon adı yanıttadır (API aşama B): kapsam dışı istasyonun ayarı da adıyla görünür.
                const stationName = setting.stationName;
                return (
                  <tr key={setting.id} className="border-t">
                    <td className="px-3 py-2">{stationName}</td>
                    <td className="px-3 py-2 text-right tabular">
                      {setting.minStock === null ? "—" : formatStockQuantity(setting.minStock)}
                    </td>
                    <td className="px-3 py-2 text-right tabular">
                      {setting.maxStock === null ? "—" : formatStockQuantity(setting.maxStock)}
                    </td>
                    <td className="px-3 py-2">{setting.shelfLocation ?? "—"}</td>
                    <td className="px-3 py-2">{setting.isListed ? t("common.yes") : t("common.no")}</td>
                    {permissions.canManageCatalog ? (
                      <td className="px-3 py-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={t("market.stationSettings.editLabel", { station: stationName })}
                          onClick={() => setSettingDialog({ setting })}
                        >
                          <Pencil aria-hidden="true" />
                          {t("common.edit")}
                        </Button>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </TableFrame>
        )}
      </Section>

      {permissions.canManageCatalog ? (
        <>
          <ProductSupplierDialog
            open={supplierDialog !== null}
            onOpenChange={(open) => !open && setSupplierDialog(null)}
            productId={card.productId}
            supplier={supplierDialog?.supplier ?? null}
          />
          <StationSettingDialog
            open={settingDialog !== null}
            onOpenChange={(open) => !open && setSettingDialog(null)}
            productId={card.productId}
            setting={settingDialog?.setting ?? null}
            stations={withoutSetting}
          />
        </>
      ) : null}
    </div>
  );
}
